import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  BUDGET_COMPATIBILITY_LABELS,
  PORTES_AGENCIAS_OPTIONS,
  type EvaluationGroupFreelancer,
  type BudgetCompatibility,
} from "@/types/evaluation";
import type { EvaluationFormItem, EvaluationResponse } from "@/types/evaluation-form";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft,
  Phone,
  MapPin,
  Briefcase,
  Clock,
  Globe2,
  ExternalLink,
  ClockAlert,
  LayoutList,
  LayoutGrid,
  HeartHandshake,
} from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MultiSelect } from "@/components/ui/multi-select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FalarComFrilaModal } from "@/components/atividades/FalarComFrilaModal";
import type { PendingActivityInfo } from "@/types/activity";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { ExternalLinkButton } from "@/components/ui/external-link-button";
import { FreelancerLinkedInButton } from "@/components/freelancers/painel/FreelancerLinkedInButton";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { toUserError } from "@/lib/api-error";

export default function EvaluationPage() {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Verifica se é modo sequencial ou um freelancer específico
  const freelancerId = searchParams.get("freelancerId");
  const isSequentialMode = searchParams.get("mode") === "sequential";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Layout do formulário (1 ou 2 colunas) - persistido no localStorage
  const [formColumns, setFormColumns] = useState<"1" | "2">(() => {
    const saved = localStorage.getItem("evaluation_form_columns");
    return (saved === "1" || saved === "2") ? saved : "1";
  });

  const handleFormColumnsChange = (value: string) => {
    if (value === "1" || value === "2") {
      setFormColumns(value);
      localStorage.setItem("evaluation_form_columns", value);
    }
  };

  // State for dynamic form items
  const [itemValues, setItemValues] = useState<Record<number, number>>({});
  
  // State for additional fields
  const [compativelGrana, setCompativelGrana] = useState<BudgetCompatibility | "">("");
  const [notaCriterioPessoal, setNotaCriterioPessoal] = useState<number>(5);
  const [agenciasAfins, setAgenciasAfins] = useState<string[]>([]);
  const [portesAgencias, setPortesAgencias] = useState<string[]>([]);
  const [outrasPercepcoes, setOutrasPercepcoes] = useState<string>("");

  // State for review modal
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewReason, setReviewReason] = useState("");
  const [isSendingToReview, setIsSendingToReview] = useState(false);

  // State for Falar com Frila modal
  const [isFalarComFrilaModalOpen, setIsFalarComFrilaModalOpen] = useState(false);
  const [pendingActivity, setPendingActivity] = useState<PendingActivityInfo | null>(null);
  const [isCheckingActivity, setIsCheckingActivity] = useState(false);

  // User profile for permissions
  const { profileName } = useUserPermissions();
  const canCreateFalarComFrila = profileName === "Admin" || profileName === "Membro";

  // Ref para armazenar os valores iniciais do formulário
  const initialFormValues = useRef<{
    itemValues: Record<number, number>;
    compativelGrana: BudgetCompatibility | "";
    notaCriterioPessoal: number;
    agenciasAfins: string[];
    portesAgencias: string[];
    outrasPercepcoes: string;
  } | null>(null);

  // Fetch group info
  const { data: group } = useQuery({
    queryKey: ["evaluation_group", groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_evaluation_groups_progress")
        .select("*")
        .eq("group_id", parseInt(groupId || "0"))
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!groupId,
  });

  // Fetch current user's evaluation weight
  const { data: userProfile } = useQuery({
    queryKey: ["user_profile_weight", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("evaluation_weight")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data as { evaluation_weight: number | null };
    },
    enabled: !!user?.id,
  });

  // Fetch freelancers
  const { data: allFreelancers = [], isLoading: isLoadingFreelancers } = useQuery({
    queryKey: ["evaluation_group_freelancers", groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_evaluation_group_freelancers_detail")
        .select("*")
        .eq("group_id", parseInt(groupId || "0"))
        .order("freelancer_name");

      if (error) throw error;
      return data as EvaluationGroupFreelancer[];
    },
    enabled: !!groupId,
  });

  // Determina a lista de freelancers a avaliar
  // No modo sequencial, mostra apenas os pendentes (não avaliados e não em revisão)
  const freelancers = isSequentialMode
    ? allFreelancers.filter((f) => f.review_status === 'pending')
    : freelancerId
    ? allFreelancers.filter((f) => f.id === parseInt(freelancerId))
    : [];

  const isSingleMode = !isSequentialMode;
  const currentFreelancer = freelancers[currentIndex];

  // Garantir que o índice não fique fora do range após refetch da lista
  useEffect(() => {
    if (freelancers.length > 0 && currentIndex >= freelancers.length) {
      setCurrentIndex(freelancers.length - 1);
    }
  }, [freelancers.length, currentIndex]);

  // Fetch Agências — key dedicada de opções (id+nome), isolada do cache de
  // shape completo em ["agencias"].
  const { data: agencias } = useQuery({
    queryKey: ["agencias", "options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select("id, nome")
        .order("nome");
      if (error) throw error;
      return data;
    },
  });

  const agenciaOptions =
    agencias?.map((a) => ({
      label: a.nome,
      value: a.id,
    })) || [];

  // Fetch form items
  const { data: formItems = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ["evaluation_form_items", group?.form_id],
    queryFn: async () => {
      if (!group?.form_id) return [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_form_items")
        .select("*")
        .eq("form_id", group.form_id)
        .order("display_order");

      if (error) throw error;
      return data as EvaluationFormItem[];
    },
    enabled: !!group?.form_id,
  });

  // Fetch existing responses for current freelancer
  const { data: existingResponses = [], isLoading: isLoadingResponses } = useQuery({
    queryKey: ["evaluation_responses", currentFreelancer?.id],
    queryFn: async () => {
      if (!currentFreelancer?.id) return [];
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("evaluation_responses")
        .select("*")
        .eq("evaluation_group_freelancer_id", currentFreelancer.id);

      if (error) throw error;
      return data as EvaluationResponse[];
    },
    enabled: !!currentFreelancer?.id,
  });

  // Reset form when changing freelancer
  useEffect(() => {
    if (formItems.length > 0 && currentFreelancer) {
      const initialValues: Record<number, number> = {};
      
      formItems.forEach((item) => {
        const existingResponse = existingResponses.find(
          (r) => r.form_item_id === item.id
        );
        
        if (existingResponse?.value !== null && existingResponse?.value !== undefined) {
          initialValues[item.id] = existingResponse.value;
        } else {
          // Default to middle value (use floor to get 5 for range 1-10)
          initialValues[item.id] = Math.floor((item.min_value + item.max_value) / 2);
        }
      });

      const initialGrana = currentFreelancer.compativel_grana || "";
      const initialNotaPessoal = currentFreelancer.nota_criterio_pessoal ?? 5;
      const initialAgencias = currentFreelancer.agencias_afins || [];
      const initialPortes = currentFreelancer.series_agencias || [];
      const initialPercepcoes = currentFreelancer.outras_percepcoes || "";

      setItemValues(initialValues);
      setCompativelGrana(initialGrana);
      setNotaCriterioPessoal(initialNotaPessoal);
      setAgenciasAfins(initialAgencias);
      setPortesAgencias(initialPortes);
      setOutrasPercepcoes(initialPercepcoes);

      // Salvar valores iniciais para comparação
      initialFormValues.current = {
        itemValues: { ...initialValues },
        compativelGrana: initialGrana,
        notaCriterioPessoal: initialNotaPessoal,
        agenciasAfins: [...initialAgencias],
        portesAgencias: [...initialPortes],
        outrasPercepcoes: initialPercepcoes,
      };
    }
  }, [formItems, existingResponses, currentFreelancer]);

  // Verificar se houve alterações no formulário
  const hasFormChanges = useMemo(() => {
    if (!initialFormValues.current) return false;

    const initial = initialFormValues.current;

    // Verificar itemValues
    const itemValuesChanged = Object.keys(itemValues).some(
      (key) => itemValues[parseInt(key)] !== initial.itemValues[parseInt(key)]
    );

    // Verificar campos adicionais
    const compativelGranaChanged = compativelGrana !== initial.compativelGrana;
    const notaPessoalChanged = notaCriterioPessoal !== initial.notaCriterioPessoal;
    const agenciasChanged =
      agenciasAfins.length !== initial.agenciasAfins.length ||
      agenciasAfins.some((a, i) => a !== initial.agenciasAfins[i]);
    const seriesChanged =
      portesAgencias.length !== initial.portesAgencias.length ||
      portesAgencias.some((s, i) => s !== initial.portesAgencias[i]);
    const percepcoesChanged = outrasPercepcoes !== initial.outrasPercepcoes;

    return (
      itemValuesChanged ||
      compativelGranaChanged ||
      notaPessoalChanged ||
      agenciasChanged ||
      seriesChanged ||
      percepcoesChanged
    );
  }, [itemValues, compativelGrana, notaCriterioPessoal, agenciasAfins, portesAgencias, outrasPercepcoes]);

  const handleItemValueChange = (itemId: number, value: number) => {
    setItemValues((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleBack = () => {
    navigate(`/avaliacoes/perfil-freelancer/${groupId}`);
  };

  const handleNextAfterSave = () => {
    // No modo sequencial, não incrementamos o índice porque o freelancer
    // avaliado será removido da lista filtrada automaticamente.
    // Apenas verificamos se ainda há freelancers para avaliar.
    // Como a query será invalidada e refetchada, a lista será atualizada
    // e o próximo freelancer aparecerá na mesma posição do índice.
    if (isSequentialMode) {
      // Se estamos na última posição da lista (pelo índice), finalizamos
      // Isso garante que ao pular freelancers e salvar o último, não voltamos aos pulados
      if (currentIndex >= freelancers.length - 1) {
        toast({
          title: "Avaliações Concluídas",
          description: "Todos os freelancers da lista foram avaliados.",
        });
        handleBack();
        return;
      }
      // Caso contrário, mantemos o índice (o próximo freelancer cairá na posição)
    } else {
      // Modo single: após salvar, volta para a lista
      handleBack();
    }
  };

  const handleNextOnSkip = () => {
    // Ao pular, precisamos incrementar o índice porque o freelancer
    // NÃO será removido da lista (continua não avaliado)
    if (currentIndex < freelancers.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      toast({
        title: "Fim da lista",
        description: "Este era o último freelancer da lista.",
      });
      handleBack();
    }
  };

  const handleSkip = () => {
    setReviewReason("");
    setIsReviewModalOpen(true);
  };

  const handleFalarComFrilaClick = async () => {
    if (!currentFreelancer || isCheckingActivity) return;

    setIsCheckingActivity(true);
    try {
      const { data, error } = await supabase.rpc("get_pending_activity_for_freelancer", {
        p_freelancer_id: currentFreelancer.freelancer_id,
        p_tipo: "falar_com_frila",
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setPendingActivity(data[0] as PendingActivityInfo);
      } else {
        setPendingActivity(null);
      }

      setIsFalarComFrilaModalOpen(true);
    } catch (error) {
      console.error("Erro ao verificar atividade:", error);
      toast({
        title: "Erro",
        description: "Falha ao verificar atividade existente.",
        variant: "destructive",
      });
    } finally {
      setIsCheckingActivity(false);
    }
  };

  const handleConfirmSendToReview = async () => {
    if (!currentFreelancer || !user) return;

    setIsSendingToReview(true);
    try {
      const { error } = await supabase.rpc('set_freelancer_in_review', {
        p_evaluation_group_freelancer_id: currentFreelancer.id,
        p_user_id: user.id,
        p_reason: reviewReason || null,
      });

      if (error) throw error;

      // Criar atividade de revisao de cadastro
      await supabase.rpc('create_freelancer_activity', {
        p_freelancer_id: currentFreelancer.freelancer_id,
        p_tipo: 'revisao_cadastro',
        p_assigned_to: null,
        p_observacao: reviewReason || null,
        p_evaluation_group_freelancer_id: currentFreelancer.id,
      });

      toast({
        title: "Enviado para revisao",
        description: `${currentFreelancer.freelancer_name} foi enviado para revisao.`,
      });

      queryClient.invalidateQueries({
        queryKey: ["evaluation_group_freelancers", groupId],
      });
      queryClient.invalidateQueries({ queryKey: ["activities"] });

      setIsReviewModalOpen(false);
      setReviewReason("");
      handleNextOnSkip();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao enviar para revisao:", error);
      toast({
        title: "Erro",
        description: "Nao foi possivel enviar para revisao. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSendingToReview(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentFreelancer || !user) return;

    setIsSubmitting(true);
    try {
      // Save responses to evaluation_responses table
      const responsesToUpsert = Object.entries(itemValues).map(([itemId, value]) => ({
        evaluation_group_freelancer_id: currentFreelancer.id,
        form_item_id: parseInt(itemId),
        value,
      }));

      for (const response of responsesToUpsert) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error: responseError } = await (supabase as any)
          .from("evaluation_responses")
          .upsert(response, {
            onConflict: "evaluation_group_freelancer_id,form_item_id",
          });

        if (responseError) throw responseError;
      }

      // Update additional fields including evaluator weight and review_status
      const { error: updateError } = await supabase
        .from("evaluation_group_freelancers")
        .update({
          compativel_grana: compativelGrana || null,
          nota_criterio_pessoal: notaCriterioPessoal,
          agencias_afins: agenciasAfins,
          series_agencias: portesAgencias,
          outras_percepcoes: outrasPercepcoes || null,
          evaluated_by: user.id,
          evaluated_at: new Date().toISOString(),
          evaluator_weight: userProfile?.evaluation_weight ?? 5,
          review_status: 'evaluated',
        })
        .eq("id", currentFreelancer.id);

      if (updateError) throw updateError;

      toast({
        title: "Avaliação salva",
        description: `Avaliação de ${currentFreelancer.freelancer_name} salva com sucesso.`,
      });

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ["evaluation_group_freelancers", groupId],
      });
      queryClient.invalidateQueries({ queryKey: ["evaluation_groups"] });
      queryClient.invalidateQueries({ queryKey: ["evaluation_responses"] });

      handleNextAfterSave();
    } catch (error: unknown) {
      const errorMessage = toUserError(error).description;
      console.error("Erro ao salvar avaliação:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a avaliação. " + errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const isLoading = isLoadingFreelancers || isLoadingItems || isLoadingResponses;
  const remainingCount = freelancers.length - currentIndex;

  if (isLoadingFreelancers) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-[600px]" />
          <Skeleton className="h-[600px]" />
        </div>
      </div>
    );
  }

  if (!currentFreelancer) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Avaliação</h1>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              {isSequentialMode
                ? "Todos os freelancers já foram avaliados! 🎉"
                : "Freelancer não encontrado."}
            </p>
            <Button onClick={handleBack} className="mt-4">
              Voltar para o grupo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isSingleMode ? "Avaliar Freelancer" : "Avaliação em Sequência"}
            </h1>
            <p className="text-muted-foreground">
              {isSingleMode 
                ? currentFreelancer.freelancer_name
                : `Falta avaliar ${remainingCount} freelancer${remainingCount > 1 ? 's' : ''}`
              }
            </p>
          </div>
        </div>
        {group && (
          <Badge variant="outline" className="text-sm">
            {group.group_name}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        {/* Left Column: Freelancer Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-primary">
                {currentFreelancer.freelancer_name}
              </CardTitle>
              {canCreateFalarComFrila && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleFalarComFrilaClick}
                  disabled={isCheckingActivity}
                  className="gap-2"
                >
                  <HeartHandshake className="h-4 w-4" />
                  Fazer Amizade
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-muted-foreground text-sm">
              {currentFreelancer.telefone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  <span>{currentFreelancer.telefone}</span>
                </div>
              )}
              {currentFreelancer.cidade && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{currentFreelancer.cidade}</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {currentFreelancer.areas?.map((area) => (
                  <Badge key={area} variant="secondary">
                    {area}
                  </Badge>
                ))}
                {currentFreelancer.idiomas?.map((idioma) => (
                  <Badge key={idioma} variant="outline">
                    {idioma}
                  </Badge>
                ))}
              </div>

              {/* Key Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg border">
                {currentFreelancer.pretensao_salarial && (
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground font-medium uppercase">
                      Pretensão Salarial
                    </span>
                    <div className="font-medium text-green-600">
                      {currentFreelancer.pretensao_salarial}
                    </div>
                  </div>
                )}
                {currentFreelancer.tempo_experiencia && (
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground font-medium uppercase">
                      Experiência
                    </span>
                    <div className="font-medium">
                      {currentFreelancer.tempo_experiencia}
                    </div>
                  </div>
                )}
                {currentFreelancer.tipo_atuacao && (
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground font-medium uppercase">
                      Atuação
                    </span>
                    <div className="font-medium">
                      {currentFreelancer.tipo_atuacao}
                    </div>
                  </div>
                )}
              </div>

              {/* Links */}
              <div className="flex gap-4">
                <ExternalLinkButton
                  kind="portfolio"
                  url={currentFreelancer.portfolio_url}
                  variant="button"
                  hideWhenEmpty={false}
                  icon={Globe2}
                  label={
                    <>
                      Portfólio
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </>
                  }
                  className="flex-1"
                />

                <FreelancerLinkedInButton
                  freelancerId={currentFreelancer.freelancer_id}
                  url={currentFreelancer.linkedin_url}
                  variant="button"
                  hideWhenEmpty={false}
                  icon={Globe2}
                  label={
                    <>
                      LinkedIn
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </>
                  }
                  className="flex-1"
                />
              </div>

              {/* Detailed Text */}
              {(currentFreelancer.historia_profissional ||
                currentFreelancer.experiencias_anteriores ||
                currentFreelancer.objetivos) && (
                <div className="space-y-6 pt-4 border-t">
                  {currentFreelancer.historia_profissional && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Briefcase className="h-4 w-4" /> História Profissional
                      </h3>
                      <div
                        className="text-sm text-muted-foreground prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(currentFreelancer.historia_profissional),
                        }}
                      />
                    </div>
                  )}
                  {currentFreelancer.experiencias_anteriores && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Experiências Anteriores
                      </h3>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {currentFreelancer.experiencias_anteriores}
                      </p>
                    </div>
                  )}
                  {currentFreelancer.objetivos && (
                    <div className="space-y-2">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ExternalLink className="h-4 w-4" /> Objetivos
                      </h3>
                      <div
                        className="text-sm text-muted-foreground prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(currentFreelancer.objetivos),
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right Column: Evaluation Form */}
        <Card className="h-fit lg:sticky lg:top-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle>Formulário de Avaliação</CardTitle>
            <ToggleGroup
              type="single"
              value={formColumns}
              onValueChange={handleFormColumnsChange}
              size="sm"
            >
              <ToggleGroupItem value="1" aria-label="1 coluna">
                <LayoutList className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="2" aria-label="2 colunas">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Dynamic form items */}
                {formItems.length > 0 ? (
                  <div className={`grid gap-4 ${formColumns === "2" ? "grid-cols-2" : "grid-cols-1"}`}>
                    {formItems.map((item) => (
                      <div key={item.id} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                            {item.label}
                          </Label>
                          <Badge variant="secondary" className="font-mono">
                            {itemValues[item.id] || item.min_value}
                          </Badge>
                        </div>
                        <Slider
                          min={item.min_value}
                          max={item.max_value}
                          step={1}
                          value={[itemValues[item.id] || item.min_value]}
                          onValueChange={(vals) => handleItemValueChange(item.id, vals[0])}
                          className="py-2"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    Nenhum item de avaliação configurado para este formulário.
                  </div>
                )}

                {/* Additional fields */}
                <div className={`grid gap-4 bg-muted/30 p-4 rounded-lg border ${formColumns === "2" ? "grid-cols-2" : "grid-cols-1"}`}>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label>Nota Critério Pessoal</Label>
                      <Badge variant="default">{notaCriterioPessoal}</Badge>
                    </div>
                    <Slider
                      min={0}
                      max={10}
                      step={0.5}
                      value={[notaCriterioPessoal]}
                      onValueChange={(vals) => setNotaCriterioPessoal(vals[0])}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Orçamento</Label>
                    <Select
                      value={compativelGrana}
                      onValueChange={(value) => setCompativelGrana(value as BudgetCompatibility)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(BUDGET_COMPATIBILITY_LABELS).map(
                          ([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Porte de Agências</Label>
                  <MultiSelect
                    options={PORTES_AGENCIAS_OPTIONS.map(opt => ({ label: opt.label, value: opt.value }))}
                    selected={portesAgencias}
                    onChange={(values) => {
                      // Limitar a 2 seleções
                      if (values.length <= 2) {
                        setPortesAgencias(values);
                      }
                    }}
                    placeholder="Selecione até 2 portes..."
                  />
                  {portesAgencias.length >= 2 && (
                    <p className="text-xs text-muted-foreground">Máximo de 2 portes selecionados</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Outras Percepções</Label>
                  <Textarea
                    value={outrasPercepcoes}
                    onChange={(e) => setOutrasPercepcoes(e.target.value)}
                    placeholder="Observações adicionais..."
                    className="min-h-[100px]"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Voltar
                  </Button>

                  {/* Botao Fazer Amizade */}
                  {canCreateFalarComFrila && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFalarComFrilaClick}
                      disabled={isCheckingActivity}
                      className="gap-2"
                    >
                      <HeartHandshake className="h-4 w-4" />
                      Fazer Amizade
                    </Button>
                  )}

                  {/* Botao de Enviar para Revisao - apenas em modo sequencial */}
                  {isSequentialMode && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSkip}
                      className="gap-2"
                    >
                      <ClockAlert className="h-4 w-4" />
                      Enviar para Revisao
                    </Button>
                  )}
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="w-full sm:w-auto">
                          <Button
                            onClick={handleSubmit}
                            size="lg"
                            disabled={isSubmitting || !hasFormChanges}
                            className="w-full"
                          >
                            {isSubmitting
                              ? "Salvando..."
                              : isSingleMode
                              ? "Salvar Avaliação"
                              : currentIndex < freelancers.length - 1
                              ? "Salvar e Próximo"
                              : "Salvar e Finalizar"}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!hasFormChanges && !isSubmitting && (
                        <TooltipContent>
                          <p>Preencha o formulário para salvar a avaliação</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog para enviar para revisão com motivo */}
      <Dialog open={isReviewModalOpen} onOpenChange={setIsReviewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar para Revisão</DialogTitle>
            <DialogDescription>
              Informe o motivo pelo qual <strong>{currentFreelancer?.freelancer_name}</strong> está sendo enviado para revisão.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="review-reason">Motivo da revisão</Label>
              <Textarea
                id="review-reason"
                placeholder="Ex: Portfólio indisponível, preciso verificar referências..."
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReviewModalOpen(false)}
              disabled={isSendingToReview}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmSendToReview}
              disabled={isSendingToReview}
            >
              {isSendingToReview ? "Enviando..." : "Enviar para Revisão"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Fazer Amizade */}
      {currentFreelancer && (
        <FalarComFrilaModal
          isOpen={isFalarComFrilaModalOpen}
          onClose={() => setIsFalarComFrilaModalOpen(false)}
          freelancerId={currentFreelancer.freelancer_id}
          freelancerNome={currentFreelancer.freelancer_name}
          existingActivity={pendingActivity}
        />
      )}
    </div>
  );
}
